import React, { useState, useEffect } from 'react';
import { Fade } from 'react-awesome-reveal';
import style from './SimpleSection.module.scss';
import { scroller } from 'react-scroll';
import { Button } from '../Button';
import SectionContainer from '../SectionContainer';
import { Typography } from '../Typography';
import { BackgroundCircle } from '../BackgroundCircle';

const SimpleSection = ({ data }: any) => {
  const [displayedText, setDisplayedText] = useState('');
  const [showFullText, setShowFullText] = useState(false);

  // Define text segments with their types
  const segments = [
    { text: 'We provide ', type: 'normal' },
    { text: 'cross-platform frameworks', type: 'highlight' },
    { text: ' for building 🔥 ', type: 'normal' },
    { text: 'high-performance, ', type: 'normal' },
    { text: 'GPU-accelerated', type: 'highlight' },
    { text: ' video plugins. Target ', type: 'normal' },
    { text: 'Adobe Premiere', type: 'highlight' },
    {
      text: ' ',
      type: 'image',
      src: '/logo/Adobe_Premiere_Pro.svg',
      alt: 'Adobe Premiere Pro',
      className: style.adobeIcon,
    },
    { text: ' and ', type: 'normal' },
    { text: 'DaVinci Resolve', type: 'highlight' },
    {
      text: ' ',
      type: 'image',
      src: '/logo/DaVinci_Resolve_Studio.svg',
      alt: 'DaVinci Resolve Studio',
      className: style.davinciIcon,
    },
    { text: ', ', type: 'normal' },
    { text: 'Final Cut Pro', type: 'highlight' },
    {
      text: ' ',
      type: 'image',
      src: '/logo/Final_Cut_Pro.svg',
      alt: 'Final Cut Pro',
      className: style.finalCutIcon,
    },
    { text: ' and ', type: 'normal' },
    { text: 'many others', type: 'highlight' },
    {
      text: ' with a simplified API for GPU operations and ',
      type: 'normal',
    },
    { text: 'plugin creation', type: 'highlight' },
    { text: '.', type: 'normal' },
  ];

  // Calculate fullText from segments
  const fullText = segments.map((seg) => seg.text).join('');

  useEffect(() => {
    if (showFullText) {
      setDisplayedText(fullText);
      return;
    }

    let currentIndex = 0;
    const typingInterval = setInterval(() => {
      if (currentIndex <= fullText.length) {
        setDisplayedText(fullText.substring(0, currentIndex));
        currentIndex++;
      } else {
        clearInterval(typingInterval);
        setShowFullText(true);
      }
    }, 70); // Speed of typing (adjust as needed)

    return () => clearInterval(typingInterval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Helper function to render text with highlights
  const renderTypedText = () => {
    // Calculate cumulative lengths
    let currentLength = 0;
    const segmentRanges = segments.map((seg) => {
      const range = {
        start: currentLength,
        end: currentLength + seg.text.length,
        ...seg,
      };
      currentLength += seg.text.length;
      return range;
    });

    // Render based on current displayedText length
    const renderedParts: (string | JSX.Element)[] = [];
    segmentRanges.forEach((range) => {
      if (range.end <= displayedText.length) {
        // Fully typed segment
        if (range.type === 'normal') {
          renderedParts.push(range.text);
        } else if (range.type === 'highlight') {
          renderedParts.push(
            <span key={renderedParts.length} className={style.highlight}>
              {range.text}
            </span>
          );
        } else if (range.type === 'image') {
          renderedParts.push(
            <img
              key={renderedParts.length}
              src={range.src}
              alt={range.alt}
              className={range.className}
            />
          );
        }
      } else if (range.start < displayedText.length) {
        // Partially typed segment
        const partialLength = displayedText.length - range.start;
        if (range.type === 'normal') {
          renderedParts.push(range.text.substring(0, partialLength));
        } else if (range.type === 'highlight') {
          renderedParts.push(
            <span key={renderedParts.length} className={style.highlight}>
              {range.text.substring(0, partialLength)}
            </span>
          );
        }
      }
    });

    return (
      <span className={style.mainTextContent}>
        {renderedParts}
        {showFullText && ' '}
        <span className={style.cursor}>|</span>
      </span>
    );
  };

  const msgSection = data?.components?.find((comp: any) => comp.name === 'msg_section');

  const handleScroll = () => {
    scroller.scrollTo('product', {
      duration: 200,
      delay: 0,
      smooth: 'easeInOutCubic',
      offset: -100,
    });
  };

  return (
    <div className={style.simpleSectionWrapper}>
      <div className={style.simpleSection}>
        <BackgroundCircle
          bottom="30px"
          left="-170px"
          width="400px"
          height="400px"
          animation="glow"
          style={{ zIndex: 1 }}
        />
        <BackgroundCircle
          top="30px"
          right="-85px"
          width="400px"
          height="400px"
          animation="float"
          animationDelay="1s"
          style={{ zIndex: 1 }}
        />

        <div className={style.content}>
          <Fade triggerOnce duration={800}>
            <Typography
              variant="display-lg"
              as="h2"
              className={style.mainText}
              color="light-primary"
              weight="medium"
              align="center"
            >
              {renderTypedText()}
            </Typography>
          </Fade>

          {!!msgSection?.CTA_text && (
            <Fade triggerOnce duration={800} delay={400}>
              <div className={style.buttonContainer}>
                <Button size="lg" onClick={handleScroll} className={style.primaryButton}>
                  {msgSection.CTA_text}
                </Button>
                <Button
                  variant="secondary"
                  size="lg"
                  onClick={handleScroll}
                  className={style.secondaryButton}
                  icon={
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <path
                        d="M5.83398 10.8333L10.0007 15L14.1673 10.8333M5.83398 5L10.0007 9.16667L14.1673 5"
                        stroke="currentColor"
                        strokeWidth="1.66667"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  }
                  iconPosition="right"
                >
                  Explore Products
                </Button>
              </div>
            </Fade>
          )}
        </div>
      </div>
    </div>
  );
};

export default SimpleSection;
